import { KeyOutlined } from '@ant-design/icons';
import { CallAdminChangeUserPasswordRequest } from '@gooduncles/gu-app-schema';
import { Alert, Button, Checkbox, Form, Input, Modal, notification } from 'antd';
import { FC, useState } from 'react';

import { useAuth } from 'src/stores/auth-context';

import { parseIfObject } from 'src/utils/common';
import { callAdminChangeUserPassword } from 'src/utils/firebase/firebase-callable';
import { AuditLogger } from 'src/utils/audit-logger';

const auditLogger = AuditLogger.getInstance();

const MIN_PASSWORD_LENGTH = 6;
const MIN_REASON_LENGTH = 5;

const ACKNOWLEDGEMENTS = [
  '대상 사용자의 매장 앱에서 자동 로그아웃되어 재로그인이 필요합니다.',
  '변경 후 이전 비밀번호로 되돌릴 수 없습니다.',
  '비밀번호는 6자 이상이어야 합니다.',
  '관리자(admin/manager) 계정의 비밀번호는 변경할 수 없습니다.',
  'v2 안정화가 끝나면 이 기능은 삭제됩니다.',
] as const;

interface FormValues {
  newPassword: string;
  reason: string;
}

interface ChangePasswordButtonProps {
  userId: string;
  userLabel?: string;
  /** 대상 사용자의 role. admin/manager인 경우 버튼을 비활성화한다. */
  targetRole?: string | null;
  /** 감사 로그/슬랙 식별용. 매장 정보 없으면 생략. */
  storeName?: string | null;
  storeCode?: string | null;
}

const ChangePasswordButton: FC<ChangePasswordButtonProps> = ({
  userId,
  userLabel,
  targetRole,
  storeName,
  storeCode,
}) => {
  const { user: operator } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState<boolean[]>(() => ACKNOWLEDGEMENTS.map(() => false));
  const [form] = Form.useForm<FormValues>();

  const canUse = operator?.role === 'admin' || operator?.role === 'manager';
  if (!canUse) {
    return null;
  }

  const isTargetAdmin = targetRole === 'admin' || targetRole === 'manager';
  const allAcknowledged = acknowledged.every(Boolean);

  const closeModal = () => {
    setOpen(false);
    setLoading(false);
    setAcknowledged(ACKNOWLEDGEMENTS.map(() => false));
    form.resetFields();
  };

  const toggleAck = (index: number, checked: boolean) => {
    if (loading) return;
    setAcknowledged((prev) => prev.map((value, i) => (i === index ? checked : value)));
  };

  const storeSuffix = (() => {
    const parts: string[] = [];
    if (storeName) parts.push(`매장명=${storeName}`);
    if (storeCode) parts.push(`매장코드=${storeCode}`);
    return parts.length ? `, ${parts.join(', ')}` : '';
  })();

  const runChange = async (values: FormValues) => {
    const newPassword = values.newPassword;
    const reason = values.reason.trim();
    setLoading(true);

    try {
      const payload: CallAdminChangeUserPasswordRequest = {
        uid: userId,
        newPassword,
        reason,
      };
      const { data } = await callAdminChangeUserPassword(payload);

      if (data.result === 'success') {
        const changedLabel = data.changedUser?.userName ?? data.changedUser?.email ?? userLabel ?? userId;
        notification.success({
          message: '비밀번호 변경 완료',
          description: changedLabel,
        });
        auditLogger.log({
          action: 'UPDATE',
          entity: 'user',
          entityId: userId,
          summary: `관리자 비밀번호 변경 - ${changedLabel}${storeSuffix}, reason=${reason}`,
          metadata: {
            reason,
            storeName: storeName ?? null,
            storeCode: storeCode ?? null,
          },
        });
        closeModal();
        return;
      }

      const reasonText = data.reason ?? '알 수 없는 에러가 발생했습니다.';
      notification.error({
        message: `비밀번호 변경 실패 (${data.errorCode ?? 'UNKNOWN'})`,
        description: reasonText,
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'user',
        entityId: userId,
        summary: `관리자 비밀번호 변경 실패(${data.errorCode ?? 'UNKNOWN'})${storeSuffix} - ${reasonText}`,
        level: 'error',
      });
    } catch (error) {
      console.error(error);
      const reasonText = parseIfObject(error);
      notification.error({
        message: '비밀번호 변경 실패',
        description: reasonText,
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'user',
        entityId: userId,
        summary: `관리자 비밀번호 변경 실패${storeSuffix} - ${reasonText}`,
        level: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    Modal.confirm({
      title: '비밀번호를 변경하시겠습니까?',
      content: `대상 계정(${userLabel ?? userId})의 Firebase Auth 비밀번호가 즉시 교체되며 되돌릴 수 없습니다.`,
      okText: '변경 진행',
      okType: 'danger',
      cancelText: '취소',
      onOk: () => runChange(values),
    });
  };

  return (
    <>
      <Button
        size='small'
        type='link'
        danger
        icon={<KeyOutlined />}
        disabled={isTargetAdmin}
        onClick={() => setOpen(true)}
      >
        비밀번호 변경
      </Button>
      <Modal
        open={open}
        title='관리자 비밀번호 변경'
        onCancel={closeModal}
        maskClosable={!loading}
        closable={!loading}
        footer={[
          <Button key='cancel' onClick={closeModal} disabled={loading}>
            취소
          </Button>,
          <Button key='submit' type='primary' danger loading={loading} disabled={!allAcknowledged} onClick={onSubmit}>
            변경
          </Button>,
        ]}
      >
        <Alert
          type='error'
          showIcon
          style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
          message='Firebase Auth 비밀번호를 직접 교체합니다'
          description={`대상 계정: ${userLabel ?? userId}\nuid: ${userId}\n반드시 본인 확인 절차를 거친 뒤 사용하세요.`}
        />

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>아래 항목을 모두 숙지했는지 확인하세요</div>
          {ACKNOWLEDGEMENTS.map((text, index) => (
            <div key={text} style={{ marginBottom: 4 }}>
              <Checkbox
                checked={acknowledged[index]}
                disabled={loading}
                onChange={(e) => toggleAck(index, e.target.checked)}
              >
                {text}
              </Checkbox>
            </div>
          ))}
        </div>

        <Form
          form={form}
          layout='vertical'
          initialValues={{ reason: 'v2 업데이트' }}
          disabled={loading || !allAcknowledged}
        >
          <Form.Item
            label='새 비밀번호'
            name='newPassword'
            rules={[
              { required: true, message: '새 비밀번호를 입력하세요.' },
              { min: MIN_PASSWORD_LENGTH, message: `비밀번호는 ${MIN_PASSWORD_LENGTH}자 이상이어야 합니다.` },
            ]}
          >
            <Input.Password
              placeholder='6자 이상'
              autoComplete='one-time-code'
              data-form-type='other'
              data-lpignore='true'
              data-1p-ignore='true'
            />
          </Form.Item>
          <Form.Item
            label='CS 사유 (감사 로그·Slack에 기록)'
            name='reason'
            rules={[
              { required: true, message: '변경 사유를 입력하세요.' },
              {
                validator: (_, value) => {
                  if (!value || value.trim().length < MIN_REASON_LENGTH) {
                    return Promise.reject(new Error(`공백 제외 ${MIN_REASON_LENGTH}자 이상 입력하세요.`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.TextArea rows={2} placeholder='예: 단말 분실로 재로그인 불가, 매장 전화로 본인 확인 완료' />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ChangePasswordButton;
